/**
 * The access decision, as a pure function.
 *
 * Authority: bead scope-njw and its 2026-08-17 amendment; PRD §4.2, §5.10, S9.
 *
 * ┌─ THE BROWSER IS NOT THE GATE ─────────────────────────────────────────────┐
 * │ `resolveRoute` decides what this browser draws. It is a *disclosure*      │
 * │ control, not an authorisation control: anyone can edit the bundle it runs │
 * │ in. The authorisation control is `withStaffSession({ roles })` in         │
 * │ `@nimer/api-auth`, and `apiAccessTable()` below is how the two are kept   │
 * │ saying the same thing — the table is derived from the same manifests the  │
 * │ SPA routes on, so a route whose roles change cannot leave the API behind. │
 * │ Never add a screen that fetches from a prefix its route does not declare. │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * The API's own status for a role mismatch is also `not-found` (scope-w2f
 * reconciled `withStaffSession`'s `assertRole` to 404, matching this file).
 *
 * Every denial is `not-found`. There is deliberately no `forbidden` outcome: a
 * consultant who is told "403" on an admin path has been told the admin path
 * exists, and the shape of the staff tree is not something a partial-privilege
 * account needs to learn.
 */
import type { StaffRole } from '@nimer/contracts/auth'

import { matchRoute } from './match.ts'
import type { RouteDefinition, RoutePlane, RouteParams, RouteRegistry } from './types.ts'

/**
 * Who is asking. Anonymous is a state, not a missing value — the auth beads
 * (scope-caw.1 for staff, scope-9xn for customers) resolve it and hand it in.
 */
export type Viewer =
  | { readonly kind: 'anonymous' }
  | { readonly kind: 'customer' }
  | { readonly kind: 'staff'; readonly roles: readonly StaffRole[] }

export const ANONYMOUS: Viewer = { kind: 'anonymous' }

export type RouteOutcome =
  | { readonly kind: 'render'; readonly route: RouteDefinition; readonly params: RouteParams }
  | { readonly kind: 'sign-in'; readonly plane: 'customer' | 'staff' }
  | { readonly kind: 'not-found' }

const NOT_FOUND: RouteOutcome = { kind: 'not-found' }

export function resolveRoute(
  registry: RouteRegistry,
  pathname: string,
  viewer: Viewer,
): RouteOutcome {
  const match = matchRoute(registry, pathname)
  if (match === null) return NOT_FOUND

  const { route, params } = match
  if (route.plane === 'public') return { kind: 'render', route, params }

  // Sign-in is offered rather than denied: before a credential exists there is
  // no role to compare, so refusing here would refuse everyone, always.
  if (viewer.kind === 'anonymous') return { kind: 'sign-in', plane: route.plane }

  // Wrong plane is wrong existence. A customer session replayed against a staff
  // path has not found a staff path.
  if (viewer.kind !== route.plane) return NOT_FOUND

  if (viewer.kind === 'staff' && !carriesRole(route, viewer.roles)) return NOT_FOUND

  return { kind: 'render', route, params }
}

/**
 * No declared roles means every recognised staff role, not "no check".
 *
 * The viewer passes on *any* declared role, matching `assertRole` in
 * `@nimer/api-auth/with-session` — the two gates must agree, or the console
 * renders a screen whose API then answers 404.
 */
function carriesRole(route: RouteDefinition, viewerRoles: readonly StaffRole[]): boolean {
  const roles = route.roles ?? []

  return roles.length === 0 || roles.some((role) => viewerRoles.includes(role))
}

export interface ApiAccessEntry {
  readonly prefix: string
  readonly plane: RoutePlane
  /** Empty means every recognised principal of the plane. */
  readonly roles: readonly StaffRole[]
}

/**
 * The table the API enforces, derived from the routes that call it.
 *
 * Roles are unioned per prefix: two screens may legitimately call one endpoint
 * from different roles, and the endpoint must accept both. Narrowing beyond
 * this is the endpoint's business — it holds the row, and the row is what says
 * whose engagement this is.
 */
export function apiAccessTable(registry: RouteRegistry): ApiAccessEntry[] {
  const byPrefix = new Map<string, { plane: RoutePlane; roles: Set<StaffRole> }>()

  for (const route of registry) {
    for (const prefix of route.api) {
      const entry = byPrefix.get(prefix) ?? { plane: route.plane, roles: new Set<StaffRole>() }
      for (const role of route.roles ?? []) entry.roles.add(role)
      byPrefix.set(prefix, entry)
    }
  }

  return [...byPrefix]
    .map(([prefix, entry]) => ({ prefix, plane: entry.plane, roles: [...entry.roles].sort() }))
    .sort((a, b) => a.prefix.localeCompare(b.prefix))
}
